import { Box, FormHelperText } from '@mui/material'
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  DateTimeInput,
  Edit,
  NumberInput,
  ReferenceInput,
  required,
  SimpleForm,
  TextInput,
  useChoicesContext,
  useNotify,
  useRedirect,
  useStore,
  useTranslate,
} from 'react-admin'

// Three fields stretched across a 1350px card made the note box a single
// line the width of the screen. A readable measure, and a note box that
// starts several lines tall.
const FORM_SX = { maxWidth: 760 }

// selectedPetId is persisted app-global state, and some writers store it as a
// string ("123"); the picker matches on the referenced record's numeric id.
const toPetId = (id: unknown) => {
  if (id == null || id === '') return undefined
  const n = Number(id)
  return Number.isFinite(n) ? n : id
}

// What tells two same-named patients apart: the client, the breed, and the patient
// id (the same "Id" the patient page header shows — the one guaranteed discriminator).
const petDetail = (pet: any) =>
  [pet?.owner?.name, pet?.breed, pet?.id != null ? `#${pet.id}` : null].filter(Boolean).join(' · ')

// Dropdown rows carry the detail. The INPUT stays the plain name: its text is sent to
// /pets/search as `q`, and a label like "Luna · Client · #123" matches nothing (0 results
// measured), so editing a prefilled field would empty the list.
const petOptionText = (pet: any) => (pet?.name ? `${pet.name} · ${petDetail(pet)}` : '')
const petInputText = (pet: any) => pet?.name ?? ''

// Under the field: WHICH patient is selected, since the input shows only the name.
const SelectedPetDetail = () => {
  const { selectedChoices } = useChoicesContext()
  return <>{petDetail(selectedChoices?.[0])}</>
}

const EmrFormFields = ({ mode }: { mode: 'create' | 'edit' }) => {
  const translate = useTranslate()
  const [selectedPetId] = useStore('selectedPetId')

  // Create ALWAYS asks which patient the note is for. selectedPetId is the
  // last patient the vet OPENED, cleared only when the account changes, and the
  // Notes list is the only way to /emrs/create — so a fixed field defaulted to it
  // silently filed the note under whoever was viewed last, showing only a bare
  // numeric id. The picker is prefilled with that patient BY NAME (a vet can see
  // and correct it) and is required, so with no patient selected it starts empty.
  // An existing note's pet is fixed: Edit reads it from the record.
  return (
    <>
      {mode === 'create' ? (
        <ReferenceInput source="pet" reference="pets">
          <AutocompleteInput
            optionText={petOptionText}
            inputText={petInputText}
            helperText={<SelectedPetDetail />}
            label={translate('resources.emrs.fields.pet')}
            defaultValue={toPetId(selectedPetId)}
            validate={required()}
          />
        </ReferenceInput>
      ) : (
        <NumberInput
          source="pet"
          label={translate('resources.emrs.fields.pet_id')}
          InputProps={{ readOnly: true }}
        />
      )}
      <DateTimeInput
        source="timestamp"
        label={translate('resources.emrs.fields.date')}
        defaultValue={new Date().toISOString()}
      />
      <TextInput
        source="notes"
        label={translate('resources.emrs.fields.notes')}
        multiline
        minRows={4}
        fullWidth
        validate={required()}
      />
      <Box sx={{ mt: 1 }}>
        <BooleanInput
          source="includeInReport"
          label={translate('pos.notes.include_in_report')}
          defaultValue={true}
        />
        <FormHelperText sx={{ mt: -1, ml: 4 }}>
          {translate('pos.notes.include_in_report_help')}
        </FormHelperText>
      </Box>
    </>
  )
}

export const EmrCreate = () => {
  const redirect = useRedirect()
  const notify = useNotify()

  const handleSuccess = (data: any, variables?: any) => {
    notify('ra.notification.created', {
      type: 'info',
      messageArgs: { smart_count: 1 },
    })

    // Back to the patient the note was SAVED for: the saved record's pet, else
    // the pet we sent. Never the store's selectedPetId — the vet may have picked
    // a different patient in the form.
    const saved = typeof data?.pet === 'object' ? data?.pet?.id : data?.pet
    const notePetId = saved ?? variables?.data?.pet
    if (notePetId != null) {
      redirect(`/pets/${notePetId}/show`)
    } else {
      redirect('list')
    }
  }

  return (
    <Create
      redirect="list"
      mutationOptions={{
        onSuccess: handleSuccess,
      }}
    >
      <SimpleForm sx={FORM_SX}>
        <EmrFormFields mode="create" />
      </SimpleForm>
    </Create>
  )
}

export const EmrEdit = () => (
  <Edit>
    <SimpleForm sx={FORM_SX}>
      <EmrFormFields mode="edit" />
    </SimpleForm>
  </Edit>
)
